import { useState, useEffect } from 'react';
import env from 'environments';
import { DashboardTemplate } from 'modules/v2/templates';
import { Button } from 'modules/v2/common/AtomicDesign/atoms';
import { getGhlSsoUrl, startGhlPasswordlessLogin } from 'modules/api/digitalServices/customer';
import LaunchModal, { STORAGE_KEY } from './components/LaunchModal';
import S from './styles';

const MarketingHub = () => {
  const [ssoData, setSsoData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [launching, setLaunching] = useState(false);
  const [error, setError] = useState(null);
  const [showLaunchModal, setShowLaunchModal] = useState(false);

  useEffect(() => {
    const fetchSsoUrl = async () => {
      try {
        const data = await getGhlSsoUrl();
        setSsoData(data);
      } catch (err) {
        // If 400 error, customer is not provisioned
        if (err?.response?.status === 400) {
          setSsoData(null);
        } else {
          console.error('Failed to fetch SSO URL:', err);
          setError(err?.response?.data?.message || 'Failed to load Marketing Hub');
        }
      } finally {
        setLoading(false);
      }
    };

    fetchSsoUrl();
  }, []);

  const launchGhl = async () => {
    if (!ssoData?.url) return;

    setLaunching(true);
    try {
      // Start passwordless login flow - backend generates a one-time code.
      // FusionAuth validates it, establishes session cookies, redirects to
      // GHL SSO URL, and GHL uses OIDC to auto-login the user.
      const passwordlessData = await startGhlPasswordlessLogin();
      window.open(passwordlessData.passwordlessLoginUrl, '_blank', 'noopener,noreferrer');
    } catch (err) {
      console.error('Failed to start passwordless login:', err);
      setError(err?.response?.data?.message || 'Failed to launch Marketing Hub. Please try again.');
    } finally {
      setLaunching(false);
    }
  };

  const handleLaunchMarketingHub = () => {
    if (!ssoData?.url) return;

    const skipModal = localStorage.getItem(STORAGE_KEY) === 'true';
    if (skipModal) {
      launchGhl();
    } else {
      setShowLaunchModal(true);
    }
  };

  // Show loading state while fetching SSO URL
  if (loading) {
    return (
      <DashboardTemplate title="Marketing Hub" subHeader={null}>
        <S.Wrapper>
          <S.LoadingContainer>
            <span>Loading...</span>
          </S.LoadingContainer>
        </S.Wrapper>
      </DashboardTemplate>
    );
  }

  // If there was an error fetching SSO URL
  if (error) {
    return (
      <DashboardTemplate title="Marketing Hub" subHeader={null}>
        <S.Wrapper>
          <S.ErrorContainer>
            <S.ErrorTitle>Error</S.ErrorTitle>
            <S.ErrorMessage>{error}</S.ErrorMessage>
            <Button
              type="outlined"
              onClick={() => window.open(`mailto:${env.SUPPORT_EMAIL}`, '_blank')}
            >
              Contact Support
            </Button>
          </S.ErrorContainer>
        </S.Wrapper>
      </DashboardTemplate>
    );
  }

  // If the user is not provisioned in GHL yet
  if (!ssoData?.url) {
    return (
      <DashboardTemplate title="Marketing Hub" subHeader={null}>
        <S.Wrapper>
          <S.NotProvisionedContainer>
            <S.NotProvisionedTitle>Marketing Hub Coming Soon</S.NotProvisionedTitle>
            <S.NotProvisionedMessage>
              Your Marketing Hub account is being set up. This feature will allow you to manage
              your social media, contacts, and marketing campaigns all in one place.
              Please check back soon or contact support if you have any questions.
            </S.NotProvisionedMessage>
            <Button
              type="outlined"
              onClick={() => window.open(`mailto:${env.SUPPORT_EMAIL}`, '_blank')}
            >
              Contact Support
            </Button>
          </S.NotProvisionedContainer>
        </S.Wrapper>
      </DashboardTemplate>
    );
  }

  return (
    <DashboardTemplate title="Marketing Hub" subHeader={null}>
      <S.Wrapper>
        <S.LaunchContainer>
          <S.LaunchIcon>
            <svg xmlns="http://www.w3.org/2000/svg" width="64" height="64" viewBox="0 0 24 24" fill="none" stroke="#3B82F6" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
              <circle cx="12" cy="12" r="10" />
              <path d="M12 2a14.5 14.5 0 0 0 0 20 14.5 14.5 0 0 0 0-20" />
              <path d="M2 12h20" />
            </svg>
          </S.LaunchIcon>
          <S.LaunchTitle>
            Marketing Hub
            <span className="ml-2 align-middle inline-flex px-2 py-0.5 text-xs font-semibold rounded bg-blue-100 text-blue-700">
              Beta
            </span>
          </S.LaunchTitle>
          <S.LaunchDescription>
            Access your all-in-one marketing platform to manage social media, contacts,
            campaigns, and more. The Marketing Hub will open in a new tab.
          </S.LaunchDescription>
          <S.FeatureList>
            <S.FeatureItem>
              <S.FeatureIcon>
                <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" />
                  <circle cx="9" cy="7" r="4" />
                  <path d="M23 21v-2a4 4 0 0 0-3-3.87" />
                  <path d="M16 3.13a4 4 0 0 1 0 7.75" />
                </svg>
              </S.FeatureIcon>
              <span>Contact Management</span>
            </S.FeatureItem>
            <S.FeatureItem>
              <S.FeatureIcon>
                <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <rect x="3" y="4" width="18" height="18" rx="2" ry="2" />
                  <line x1="16" y1="2" x2="16" y2="6" />
                  <line x1="8" y1="2" x2="8" y2="6" />
                  <line x1="3" y1="10" x2="21" y2="10" />
                </svg>
              </S.FeatureIcon>
              <span>Social Media Scheduling</span>
            </S.FeatureItem>
            <S.FeatureItem>
              <S.FeatureIcon>
                <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z" />
                  <polyline points="22,6 12,13 2,6" />
                </svg>
              </S.FeatureIcon>
              <span>Email & SMS Campaigns</span>
            </S.FeatureItem>
            <S.FeatureItem>
              <S.FeatureIcon>
                <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <line x1="18" y1="20" x2="18" y2="10" />
                  <line x1="12" y1="20" x2="12" y2="4" />
                  <line x1="6" y1="20" x2="6" y2="14" />
                </svg>
              </S.FeatureIcon>
              <span>Analytics & Reporting</span>
            </S.FeatureItem>
          </S.FeatureList>
          <Button type="primary" onClick={handleLaunchMarketingHub} disabled={launching}>
            {launching ? 'Launching...' : 'Launch Marketing Hub'}
          </Button>
        </S.LaunchContainer>
      </S.Wrapper>
      <LaunchModal
        show={showLaunchModal}
        onContinue={() => {
          setShowLaunchModal(false);
          launchGhl();
        }}
        onClose={() => setShowLaunchModal(false)}
      />
    </DashboardTemplate>
  );
};

export default MarketingHub;
